import { z } from 'zod';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { ModelName } from '@casops/store/api';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import useModelForm from '@casops/components/layout/model-form/useModelForm';
import { useAddUserMutation, useEditUserMutation } from '@casops/store/apis/usersApi';
import CasFormMultiselect from '@cas/components/inputs/cas-form-multiselect/CasFormMultiselect';
import {
  UserCreateModel,
  UserFormModel,
  UserUnionModel,
  UserUpdateModel,
} from '@casops/store/apis/usersApi';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import { ServerErrorFields } from '@dmc/ui';
import { useFormEditMode } from '@casops/components/layout/model-form/useFormEditMode';
import { UserRole } from '@casops/store/apis/settingsApi';

const NAME_MAX = 50;

type DataModel = UserUnionModel;
type FormModel = UserFormModel;
type CreateModel = UserCreateModel;
type EditModel = UserUpdateModel;

const modelName = ModelName.User;

const defaultValues: FormModel = {
  un: '',
  fn: '',
  ln: '',
  em: '',
  pa: '',
  cp: '',
  roles: [],
  ro: false,
};

const getCreateArgs = ({ un, fn, ln, em, roles, pa }: FormModel): CreateModel => {
  return { un, fn, ln, em, roles, pa };
};

const getEditArgs = ({ fn, ln, em, roles }: FormModel): EditModel => {
  return { fn, ln, em, roles };
};

// Note: These are currently the only fields with custom validation responses from the API.
const serverErrorFields: ServerErrorFields<FormModel> = {
  un: 'Username',
  em: 'Email',
};

interface HoldTimeFormProps {
  item?: DataModel;
  getRoleOptions: () => DropdownItemProps<UserRole>[];
}

export const UserForm = ({ item, getRoleOptions }: HoldTimeFormProps): ReactElement => {
  const { t } = useTranslation();

  const editMode = useFormEditMode();

  const schema = useMemo(() => {
    const commonSchemaFields = {
      fn: z
        .string()
        .min(1, { message: t('Validation.SpecifyFirstName') })
        .max(NAME_MAX, { message: t('Validation.NameTooLong', { max: NAME_MAX }) }),
      ln: z
        .string()
        .min(1, { message: t('Validation.SpecifyLastName') })
        .max(NAME_MAX, { message: t('Validation.NameTooLong', { max: NAME_MAX }) }),
      em: z
        .string()
        .email({ message: t('Validation.SpecifyEmail') })
        .max(128, { message: t('Validation.EmailTooLong') }),
      roles: z.array(z.nativeEnum(UserRole)),
    };

    // Return the user edit schema when in edit mode.
    if (editMode) {
      return z.object({ ...commonSchemaFields });
    }

    // Otherwise return the user create schema.
    return z
      .object({
        ...commonSchemaFields,
        un: z
          .string()
          .min(1, { message: t('Validation.SpecifyUsername') })
          .max(128, { message: t('Validation.UsernameTooLong') }),
        // Password validation is configured in the backend, so only check for a non-empty password.
        pa: z.string().min(1, { message: t('Validation.SpecifyPassword') }),
        cp: z.string().min(1, { message: t('Validation.ConfirmPassword') }),
      })
      .superRefine(({ pa, cp }, ctx) => {
        if (cp !== pa) {
          ctx.addIssue({
            code: 'custom',
            message: t('Validation.PasswordsDoNotMatch'),
            path: ['cp'],
          });
        }
      });
  }, [t, editMode]);

  const [triggerCreateItem] = useAddUserMutation();
  const [triggerEditItem] = useEditUserMutation();

  const { canSubmit, onSubmit, errorMessage, control } = useModelForm<
    DataModel,
    FormModel,
    CreateModel,
    EditModel
  >({
    modelName,
    defaultValues,
    item,
    schema,
    triggerCreateItem,
    getCreateArgs,
    triggerEditItem,
    getEditArgs,
    serverErrorFields,
  });

  return (
    <ModelForm
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      control={control}
    >
      <>
        <CasFormInput
          key="un"
          control={control}
          name="un"
          label={t('Fields.Username')}
          disabled={editMode}
        />

        <CasFormInput key="fn" control={control} name="fn" label={t('Fields.FirstName')} />
        <CasFormInput key="ln" control={control} name="ln" label={t('Fields.LastName')} />
        <CasFormInput key="em" control={control} name="em" label={t('Fields.Email')} />

        <CasFormMultiselect<UserFormModel, UserRole>
          key="roles"
          control={control}
          placeholder={t('Fields.RolesPlaceholder')}
          name="roles"
          label={t('Fields.Roles')}
          useItemsHook={getRoleOptions}
          disabled={item?.ro}
        />

        {!editMode && (
          <>
            <CasFormInput
              key="pa"
              type="password"
              name="pa"
              control={control}
              label={t('Fields.Password')}
              autocomplete="new-password"
            />
            <CasFormInput
              key="cp"
              type="password"
              name="cp"
              control={control}
              label={t('Fields.ConfirmPassword')}
              autocomplete="new-password"
            />
          </>
        )}
      </>
    </ModelForm>
  );
};

export default UserForm;
